import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { StarIcon } from '@heroicons/react/20/solid';
import { StarIcon as StarIconOutline } from '@heroicons/react/24/outline';
import {
  ShieldCheckIcon,
  LockClosedIcon,
  ArrowPathIcon,
  CheckIcon,
  // WrenchScrewdriverIcon,
  BoltIcon,
  GiftIcon,
  CreditCardIcon,
  ChatBubbleLeftIcon,
  EnvelopeIcon,
  PhoneIcon,
  ClockIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchProductByIdAsync,
  selectProductById,
  selectAllProducts,
  selectProductListStatus,
  clearSelectedProduct,
  setSelectedProduct,
} from '../productSlice';
import { useParams, useNavigate } from 'react-router-dom';
import { addToCartAsync, selectItems } from '../../cart/cartSlice';
import { selectLoggedInUser } from '../../auth/authSlice';
import { useAlert } from 'react-alert';
import { Grid } from 'react-loader-spinner';
import { useLanguage } from '../../../context/LanguageContext';
import { useProductDetailTranslations } from '../../../context/productDetailTranslations';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function ProductDetail() {
  const items = useSelector(selectItems);
  const selectedProduct = useSelector(selectProductById);
  const allProducts = useSelector(selectAllProducts);
  const user = useSelector(selectLoggedInUser);
  const dispatch = useDispatch();
  const params = useParams();
  const navigate = useNavigate();
  const alert = useAlert();
  const status = useSelector(selectProductListStatus);

  // Optimistic resolution:
  // 1. If selectedProduct matches current URL params.id, use it instantly.
  // 2. Otherwise search allProducts (already in Redux from Homepage / Products catalog).
  // 3. Fallback to null only if opening a fresh direct link in a new tab.
  const product = (selectedProduct && selectedProduct.id === params.id)
    ? selectedProduct
    : (allProducts.find((p) => p.id === params.id) || null);
  const { t, language } = useLanguage();
  const pt = useProductDetailTranslations(language);
  const [planType, setPlanType] = useState('monthly'); // 'monthly' or 'yearly'

  // Helper function to get multilingual text from product
  const getMultilingualText = (field) => {
    if (!field) return '';
    // If field is a string, return it as-is
    if (typeof field === 'string') return field;
    // If field is an object with language keys
    if (typeof field === 'object') {
      // Try current language first, then English, then any available language
      const result = field[language] || field['en'] || field['fr'] || Object.values(field)[0] || '';
      // Ensure we always return a string, never an object
      if (typeof result === 'string') return result;
      return '';
    }
    return '';
  };

  // Safe render helper - ensures we never render objects
  // const safeRender = (value) => {
  //   if (value === null || value === undefined) return '';
  //   if (typeof value === 'string' || typeof value === 'number') return value;
  //   if (typeof value === 'object' && value[language]) return value[language];
  //   if (typeof value === 'object' && value['en']) return value['en'];
  //   console.warn('safeRender encountered non-string object:', value);
  //   return '';
  // };

  // Safe JSX render - prevents rendering objects as children
  // const safeJSXRender = (value, fieldName = 'unknown') => {
  //   if (value === null || value === undefined) return '';
  //   if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
  //   if (typeof value === 'object') {
  //     console.error(`ERROR: Attempting to render object as JSX for field "${fieldName}":`, value);
  //     if (value[language]) return value[language];
  //     if (value['en']) return value['en'];
  //     if (value['fr']) return value['fr'];
  //     const firstValue = Object.values(value)[0];
  //     if (typeof firstValue === 'string') return firstValue;
  //   }
  //   return '';
  // };

  const handleCart = (e) => {
    e.preventDefault();
    // Check if user is logged in
    if (!user) {
      alert.info('Please login to add items to cart');
      navigate('/login');
      return;
    }
    if (items.findIndex((item) => item.product.id === product.id) < 0) {
      const newItem = {
        product: product.id,
        quantity: 1,
        planType: planType,
      };
      dispatch(addToCartAsync({ item: newItem, alert }));
    } else {
      alert.error('Item Already added');
    }
  };

  useEffect(() => {
    // Instant scroll to top on navigation so user never sees page offset
    window.scrollTo({ top: 0, behavior: 'instant' });

    // If product is found optimistically in allProducts but selectedProduct is not set, sync it immediately
    if (product && (!selectedProduct || selectedProduct.id !== params.id)) {
      dispatch(setSelectedProduct(product));
    }

    // Background fetch to ensure latest data from server without blocking UI
    if (!selectedProduct || selectedProduct.id !== params.id) {
      dispatch(fetchProductByIdAsync(params.id));
    }
  }, [params.id]);

  // Preload product image for faster rendering
  useEffect(() => {
    if (product?.thumbnail) {
      const img = new Image();
      img.src = product.thumbnail;
      // Image will be cached in browser for faster display
    }
  }, [product?.thumbnail]);

  // Set language to English by default on page load if not set
  useEffect(() => {
    if (!localStorage.getItem('language')) {
      localStorage.setItem('language', 'en');
    }
  }, []);

  // Determine available pricing options
  const hasMonthlyPrice = product?.price && product?.discountPrice;
  const hasYearlyPrice = product?.yearlyPrice && product?.yearlyDiscountPrice;

  // Set default plan type based on availability
  useEffect(() => {
    if (product && !hasMonthlyPrice && hasYearlyPrice) {
      setPlanType('yearly');
    } else if (product && hasMonthlyPrice) {
      setPlanType('monthly');
    }
  }, [hasMonthlyPrice, hasYearlyPrice, product]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' },
    },
  };

  const scaleVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: 'easeOut' },
    },
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {!product && status === 'loading' ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <Grid
            height="80"
            width="80"
            color="#2563eb"
            ariaLabel="grid-loading"
            radius="12.5"
            wrapperStyle={{}}
            wrapperClass=""
            visible={true}
          />
        </div>
      ) : null}
      {!product && status !== 'loading' && (
        <div className="flex flex-col items-center justify-center min-h-[400px] text-center px-4">
          <p className="text-xl font-semibold text-gray-700 mb-4">{t.productNotFound || 'Product not found'}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm"
          >
            Back to Home
          </button>
        </div>
      )}
      {product && (
        <motion.div
          className="py-6 sm:py-8 lg:py-10"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {/* Main Product Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:p-8 mb-8">
              <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                {/* Image Section */}
                <motion.div
                  className="order-first"
                  variants={scaleVariants}
                >
                  <div className="sticky top-20 space-y-4">
                    <div className="overflow-hidden rounded-2xl border-2 border-gray-200 shadow-lg">
                      <img
                        src={product.thumbnail}
                        alt={getMultilingualText(product.title)}
                        className="w-full h-auto object-cover hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    
                    {/* Trust Badges Below Image */}
                    <div className="grid grid-cols-3 gap-2">
                      {product.trustBadges?.isGenuine && (
                        <div className="text-center p-3 rounded-lg bg-blue-50 border border-blue-200">
                          <ShieldCheckIcon className="w-6 h-6 mx-auto text-blue-600 mb-1" />
                          <p className="text-xs font-medium text-gray-700">{pt.genuineLabel}</p>
                        </div>
                      )}
                      {product.trustBadges?.isSecure && (
                        <div className="text-center p-3 rounded-lg bg-blue-50 border border-blue-200">
                          <LockClosedIcon className="w-6 h-6 mx-auto text-blue-600 mb-1" />
                          <p className="text-xs font-medium text-gray-700">{pt.secureLabel}</p>
                        </div>
                      )}
                      {product.trustBadges?.moneyBackGuaranteed && (
                        <div className="text-center p-3 rounded-lg bg-blue-50 border border-blue-200">
                          <ArrowPathIcon className="w-6 h-6 mx-auto text-blue-600 mb-1" />
                          <p className="text-xs font-medium text-gray-700">{pt.moneyBackLabel}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>

                {/* Product Details Section */}
                <motion.div variants={containerVariants}>
                  <motion.div variants={itemVariants} className="space-y-6">
                    {/* Title */}
                    <div>
                      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight mb-3">
                        {product && product.title ? getMultilingualText(product.title) : 'Loading...'}
                      </h1>
                      
                      {/* Rating */}
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-0.5">
                          {[0, 1, 2, 3, 4].map((rating) => (
                            <StarIcon
                              key={rating}
                              className={classNames(
                                product.rating > rating ? 'text-yellow-400' : 'text-gray-300',
                                'h-5 w-5'
                              )}
                              aria-hidden="true"
                            />
                          ))}
                        </div>
                        <span className="text-sm text-gray-600">
                          {product.rating} {pt.outOf} 5
                        </span>
                        <span className="text-sm text-gray-400">|</span>
                        <span className={`text-sm font-medium ${product.stock > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {product.stock > 0 ? `${product.stock} in stock` : pt.outOfStock}
                        </span>
                      </div>
                    </div>

                    {/* Price Section */}
                    <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
                      {/* Discount Badge */}
                      {((planType === 'monthly' && product.discountPercentage > 0) ||
                        (planType === 'yearly' && product.yearlyDiscountPercentage > 0)) && (
                        <div className="flex items-center gap-2 mb-3">
                          <span className="inline-flex items-center gap-1 bg-red-500 text-white px-3 py-1 rounded-lg text-sm font-bold">
                            -{planType === 'monthly' ? product.discountPercentage : product.yearlyDiscountPercentage}% OFF
                          </span>
                          <span className="text-xs text-gray-500 font-medium uppercase">
                            {pt.limitedTimeOffer}
                          </span>
                        </div>
                      )}

                      {/* Price Display */}
                      <div className="flex items-baseline gap-3 mb-2">
                        <span className="text-4xl font-bold text-blue-600">
                          €{planType === 'monthly'
                            ? product.discountPrice || product.price
                            : product.yearlyDiscountPrice || product.yearlyPrice}
                        </span>
                        {((planType === 'monthly' && product.discountPercentage > 0) ||
                          (planType === 'yearly' && product.yearlyDiscountPercentage > 0)) && (
                          <span className="text-xl text-gray-400 line-through">
                            €{planType === 'monthly' ? product.price : product.yearlyPrice}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">
                        {planType === 'monthly' ? pt.billedMonthly : pt.billedYearly}
                      </p>
                    </div>

                    {/* Plan Type Selector - Dropdown Style */}
                    {hasMonthlyPrice && hasYearlyPrice && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Select Plan
                        </label>
                        <div className="relative">
                          <select
                            value={planType}
                            onChange={(e) => setPlanType(e.target.value)}
                            className="w-full appearance-none bg-white border-2 border-gray-200 rounded-xl px-4 py-3.5 pr-10 text-gray-900 font-medium focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all cursor-pointer"
                          >
                            <option value="monthly">{t('monthly')} - €{product.discountPrice || product.price}</option>
                            <option value="yearly">{t('yearly')} - €{product.yearlyDiscountPrice || product.yearlyPrice}</option>
                          </select>
                          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Show pricing type badge if only yearly available */}
                    {!hasMonthlyPrice && hasYearlyPrice && (
                      <div className="bg-blue-100 border border-blue-300 rounded-xl p-3">
                        <p className="text-sm font-semibold text-blue-700">
                          📅 {t('yearly')} Plan Only
                        </p>
                      </div>
                    )}

                    {/* Add to Cart Button */}
                    <div className="space-y-3">
                      <button
                        onClick={handleCart}
                        disabled={product.stock === 0}
                        type="button"
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl text-base font-bold uppercase tracking-wide disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        {pt.addToCart}
                      </button>
                      
                      {/* Contact for Bulk Orders */}
                      <a
                        href="https://wa.me/923272341839"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.67-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.076 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                        WhatsApp for Order
                      </a>
                    </div>

                    {/* Quick Info */}
                    <div className="grid grid-cols-2 gap-3 pt-4 border-t border-gray-200">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <TruckIcon className="w-5 h-5 text-blue-600" />
                        <span>Instant Delivery</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <ShieldCheckIcon className="w-5 h-5 text-blue-600" />
                        <span>100% Genuine</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <CreditCardIcon className="w-5 h-5 text-blue-600" />
                        <span>Secure Payment</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <ChatBubbleLeftIcon className="w-5 h-5 text-blue-600" />
                        <span>24/7 Support</span>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              </div>
            </div>

            {/* Description Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:p-8 mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-1 h-6 bg-blue-600 rounded-full"></span>
                {t('description')}
              </h2>
              <p className="text-gray-600 leading-relaxed">
                {product && product.description ? getMultilingualText(product.description) : 'No description available'}
              </p>
            </div>

            {/* Highlights Section */}
            {product.highlights && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:p-8 mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <span className="w-1 h-6 bg-blue-600 rounded-full"></span>
                  {pt.featuresAndBenefits}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(() => {
                    let highlightsArray = [];
                    if (Array.isArray(product.highlights)) {
                      highlightsArray = product.highlights;
                    } else if (typeof product.highlights === 'object') {
                      highlightsArray = product.highlights[language] || product.highlights['en'] || [];
                    }
                    return highlightsArray.map((highlight, index) => (
                      <div
                        key={index}
                        className="flex gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
                      >
                        <div className="flex-shrink-0">
                          <div className="flex items-center justify-center h-5 w-5 rounded-full bg-green-500 text-white">
                            <CheckIcon className="w-3 h-3" />
                          </div>
                        </div>
                        <p className="text-sm text-gray-700">
                          {getMultilingualText(highlight)}
                        </p>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )}

            {/* How It Works Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:p-8 mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-1 h-6 bg-blue-600 rounded-full"></span>
                {pt.howItWorks}
              </h2>
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <p className="text-gray-700 leading-relaxed mb-3">
                  {getMultilingualText(product.comments)}
                </p>
                <div className="flex items-center gap-2 text-blue-600 font-medium text-sm">
                  <TruckIcon className="w-5 h-5" />
                  <span>{pt.instantDelivery}</span>
                </div>
              </div>
            </div>

            {/* Why Should You Buy Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:p-8 mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <span className="w-1 h-6 bg-blue-600 rounded-full"></span>
                {pt.whyChooseThis}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Features Column 1 */}
                <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
                  <h3 className="font-semibold text-gray-900 text-base mb-4 flex items-center gap-2">
                    <BoltIcon className="w-5 h-5 text-blue-600" />
                    {pt.advancedFeatures}
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">
                        {pt.professionalTools}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">
                        {pt.unlimitedAccess}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">
                        {pt.dedicatedSupport}
                      </span>
                    </li>
                  </ul>
                </div>

                {/* Features Column 2 */}
                <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
                  <h3 className="font-semibold text-gray-900 text-base mb-4 flex items-center gap-2">
                    <GiftIcon className="w-5 h-5 text-blue-600" />
                    {pt.benefits}
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">
                        {pt.saveTime}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">
                        {pt.itemsUnlocked}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">
                        {pt.plansAvailable}
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Customer Reviews Section */}
            {product.reviews && product.reviews.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:p-8 mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <span className="w-1 h-6 bg-blue-600 rounded-full"></span>
                  {pt.customerReviews}
                </h2>
                <div className="space-y-4">
                  {product.reviews.map((review, index) => (
                    <div
                      key={index}
                      className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                    >
                      {/* Customer Name and Rating */}
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-3">
                        <h3 className="text-base font-semibold text-gray-900">
                          {getMultilingualText(review.customerName)}
                        </h3>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-0.5">
                            {[0, 1, 2, 3, 4].map((star) => (
                              <span key={star}>
                                {star < review.reviewRating ? (
                                  <StarIcon className="w-4 h-4 text-yellow-400" />
                                ) : (
                                  <StarIconOutline className="w-4 h-4 text-gray-300" />
                                )}
                              </span>
                            ))}
                          </div>
                          <span className="text-xs font-semibold text-blue-600 bg-blue-100 px-2 py-1 rounded">
                            {review.reviewRating}/5
                          </span>
                        </div>
                      </div>

                      {/* Review Text */}
                      <p className="text-sm text-gray-600 leading-relaxed">
                        "{getMultilingualText(review.reviewText)}"
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Payment & Support Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:p-8 mb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Payment Methods */}
                <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
                  <h3 className="font-semibold text-gray-900 text-base mb-4 flex items-center gap-3">
                    <CreditCardIcon className="w-5 h-5 text-blue-600" />
                    {pt.paymentMethods}
                  </h3>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>Western Union</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>Bank Transfer</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>Revolut</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>Crypto (Binance)</span>
                    </li>
                  </ul>
                </div>

                {/* Support */}
                <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
                  <h3 className="font-semibold text-gray-900 text-base mb-4 flex items-center gap-3">
                    <ChatBubbleLeftIcon className="w-5 h-5 text-blue-600" />
                    {pt.needHelp}
                  </h3>
                  <div className="space-y-2">
                    <p className="flex items-center gap-2 text-sm text-gray-700">
                      <EnvelopeIcon className="w-4 h-4 text-blue-600 flex-shrink-0" />
                      <a href="mailto:contact@subifyhub.com" className="text-blue-600 hover:underline">
                        contact@subifyhub.com
                      </a>
                    </p>
                    <p className="flex items-center gap-2 text-sm text-gray-700">
                      <PhoneIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
                      <a href="https://wa.me/923272341839" className="text-green-600 hover:underline">
                        WhatsApp: +92 327 2341839
                      </a>
                    </p>
                    <p className="flex items-center gap-2 text-sm text-gray-700">
                      <ChatBubbleLeftIcon className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <a href="https://t.me/923272341839" className="text-blue-500 hover:underline">
                        Telegram: +92 327 2341839
                      </a>
                    </p>
                    <p className="flex items-center gap-2 text-sm text-gray-700">
                      <ClockIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                      10 AM - 11 PM (PKT)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}
